import type { TabsProps } from 'antd';
import {
    AppstoreOutlined,
    ShoppingCartOutlined,
    LineChartOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Form, type FormInstance, Tabs } from 'antd';
import type { LogFilter } from '@/features/log/types/Log';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import { useEmpresaConfiguracaoQuery } from '@/features/empresa/api/apiEmpresaConfiguracaoGet';
import { OrigemEnum } from '@/features/log/types/enum';
import { ProdutoTabKeyHistoricoAlteracao } from '../useProdutoTabItems';
import { ProdutoTabHistoricoAlteracaoCusto } from './ProdutoTabHistoricoAlteracaoCusto';
import { ProdutoTabHistoricoAlteracaoCustoMedio } from './ProdutoTabHistoricoAlteracaoCustoMedio';
import { ProdutoTabHistoricoAlteracaoCustoVenda } from './ProdutoTabHistoricoAlteracaoCustoVenda';
import { ProdutoTabHistoricoAlteracaoGeral } from './ProdutoTabHistoricoAlteracaoGeral';

function isProdutoTabKey(value: string): value is ProdutoTabKeyHistoricoAlteracao {
    return Object.values(ProdutoTabKeyHistoricoAlteracao).includes(
        value as ProdutoTabKeyHistoricoAlteracao,
    );
}

export function ProdutoTabHistoricoAlteracao({ form }: { form: FormInstance<FormProdutoValues> }) {
    const navigate = useNavigate({ from: '/a/produto/$action/{-$idpk}' });

    const { tab2 } = useSearch({ from: '/a/produto/$action/{-$idpk}' }) as {
        tab2: ProdutoTabKeyHistoricoAlteracao;
    };

    const { empresaConfig } = useEmpresaConfiguracaoQuery();

    const pro_idpk = Form.useWatch('pro_idpk', form);

    const filter: LogFilter | undefined = pro_idpk
        ? { log_origem: OrigemEnum.Produto, log_origem_idpk: pro_idpk }
        : undefined;

    const enabled = tab2 === ProdutoTabKeyHistoricoAlteracao.Geral && !!pro_idpk;

    const items: TabsProps['items'] = [];

    if (empresaConfig?.emc_log_gerar === 'S') {
        items.push({
            children: <ProdutoTabHistoricoAlteracaoGeral enabled={enabled} filter={filter} />,
            icon: <AppstoreOutlined />,
            key: 'geral',
            label: 'Geral',
        });
    }

    items.push(
        {
            children: <ProdutoTabHistoricoAlteracaoCusto form={form} tab2={tab2} />,
            icon: <ShoppingCartOutlined />,
            key: 'preco-custo',
            label: 'Preço de custo',
        },
        {
            children: <ProdutoTabHistoricoAlteracaoCustoMedio form={form} tab2={tab2} />,
            icon: <LineChartOutlined />,
            key: 'custo-medio',
            label: 'Custo médio',
        },
        {
            children: <ProdutoTabHistoricoAlteracaoCustoVenda form={form} tab2={tab2} />,
            icon: <DollarOutlined />,
            key: 'preco-venda',
            label: 'Preço de venda',
        },
    );

    return (
        <Tabs
            activeKey={tab2}
            items={items}
            onChange={(k) => {
                if (!isProdutoTabKey(k)) {
                    return;
                }

                void navigate({
                    search: (prev) => ({
                        ...prev,
                        tab2: k,
                    }),
                });
            }}
        />
    );
}
